import { defineStore } from 'pinia';
import { ref, watch } from 'vue';
import type { OpenDetailInfo } from '@/dashboard/features/detailIntegration/composables/useDashboardDetailAdapter';
import type { WidgetTriggerPathType, WidgetUUID } from '@/common/utils/types';
import type {
  SummaryDetailData,
  SummaryDetailType,
} from '@/dashboard/features/detailIntegration/summaryDetailSlide/types/summaryDetailSlide.types';
import type { SlideDetailAdapterResult } from '@/common/types/slideDetailAdapter.types';

export interface TriggerDetailInfo {
  triggerPath: WidgetTriggerPathType | null;
  widgetId: WidgetUUID;
  targetId: string;
  targetName?: string;
  rawData?: Record<string, any>;
  addition?: Record<string, any>;
  subTargetId?: string;
  selectedTab?: string;
  /**
   * 위젯 API 호출에 사용된 fromTime/toTime.
   * 삭제된 타겟 detail 연계 시 timestamp 손실을 방지하기 위해 caller가 항상 전달한다.
   * adapter에서 addition으로 병합되며, addition.fromTime/toTime이 있으면 그 쪽이 우선한다.
   */
  fromTime?: string;
  toTime?: string;
}

/**
 * Dashboard Detail 전용 store
 * - detail slide / summary detail / window popup / alert detail 상태 관리
 * - adaptor를 통한 detail 슬라이드 오픈 상태 관리
 * - 마지막 열린 detail 결과 추적
 */
export const useDashboardDetailStore = defineStore('dashboardDetail', () => {
  const isDetailOpening = ref(false);
  const latestDetailRequest = ref<symbol | null>(null);
  const lastTriggerInfo = ref<OpenDetailInfo | null>(null);
  const lastOpenResult = ref<SlideDetailAdapterResult | null>(null);

  const setDetailOpening = (value: boolean) => {
    isDetailOpening.value = value;
  };

  const setLastResult = (triggerInfo: OpenDetailInfo, result: SlideDetailAdapterResult) => {
    lastTriggerInfo.value = triggerInfo;
    lastOpenResult.value = result;
  };

  const reset = () => {
    isDetailOpening.value = false;
    latestDetailRequest.value = null;
    lastTriggerInfo.value = null;
    lastOpenResult.value = null;
  };

  // ── Detail Slide 상태 ─────────────────────────────────────────────────
  const selectedDetailInfo = ref<TriggerDetailInfo | null>(null);
  const isShowSlideDetail = ref(false);

  const initSelectedDetailInfo = () => {
    selectedDetailInfo.value = null;
  };

  watch(isShowSlideDetail, (value) => {
    if (!value) {
      initSelectedDetailInfo();
    }
  });

  // ── Summary Detail Slide 상태 ─────────────────────────────────────────
  const isShowSummaryDetailSlide = ref<boolean>(false);
  const summaryDetailData = ref<SummaryDetailData<SummaryDetailType> | null>(null);

  /** 서브 상세(summary detail) 슬라이드 닫기 */
  const closeSummaryDetail = () => {
    isShowSummaryDetailSlide.value = false;
    summaryDetailData.value = null;
  };

  const openSummaryDetail = (data: SummaryDetailData) => {
    isShowSlideDetail.value = false;
    latestDetailRequest.value = null;

    summaryDetailData.value = data;
    selectedDetailInfo.value = null;
    isShowSummaryDetailSlide.value = true;
  };

  // ── Detail Window Popup 상태 ──────────────────────────────────────────
  const isShowDetailWindowPopup = ref(false);
  const selectedDetailWindowPopupInfo = ref<TriggerDetailInfo | null>(null);

  const setDetailWindowPopupInfo = (detailInfo: TriggerDetailInfo) => {
    selectedDetailWindowPopupInfo.value = detailInfo;
    isShowDetailWindowPopup.value = true;
  };

  const initDetailPopupWindow = () => {
    isShowDetailWindowPopup.value = false;
    selectedDetailWindowPopupInfo.value = null;
  };

  watch(isShowDetailWindowPopup, (value) => {
    if (!value) {
      initDetailPopupWindow();
    }
  });

  // ── Alert Detail 상태 ─────────────────────────────────────────────────
  const isShowAlertDetail = ref(false);

  const setIsShowAlertDetail = (value: boolean) => {
    isShowAlertDetail.value = value;
  };

  return {
    isDetailOpening,
    latestDetailRequest,
    lastTriggerInfo,
    lastOpenResult,
    setDetailOpening,
    setLastResult,
    reset,

    selectedDetailInfo,
    isShowSlideDetail,
    initSelectedDetailInfo,

    isShowSummaryDetailSlide,
    summaryDetailData,
    closeSummaryDetail,
    openSummaryDetail,

    isShowDetailWindowPopup,
    selectedDetailWindowPopupInfo,
    setDetailWindowPopupInfo,
    initDetailPopupWindow,

    isShowAlertDetail,
    setIsShowAlertDetail,
  };
});
